import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  useTheme,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import CoreValues from '../sections/CoreValues';
import PageHeader from '../ui/PageHeader';

const AboutUs = () => {
  const theme = useTheme();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCoach, setActiveCoach] = useState(null);

  useEffect(() => {
    fetch('/data/coaches.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load coaches data');
        }
        return response.json();
      })
      .then(data => {
        setCoaches(data.coaches.filter(coach => coach.active));
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading coaches:', err);
        setError('Failed to load coaches data. Please try again later.');
        setLoading(false);
      });
  }, []);

  return (
    <Container sx={{ mt: 4 }}>
      <PageHeader title="About Us" />

      <Grid
        component={motion.div}
        container
        spacing={{ xs: 3, md: 5 }}
        alignItems="stretch"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6 }}
        sx={{ mb: 8 }}
      >
        {/* Image */}
        <Grid item xs={12} md={5}>
          <Box
            component="img"
            src="/images/SJ_Gallery/SQ_1_5.jpg"
            alt="SJ Swimming coaches in the pool"
            sx={{
              width: '100%',
              height: { xs: 260, md: '100%' },
              minHeight: { md: 340 },
              objectFit: 'cover',
              borderRadius: '20px',
              boxShadow: '0px 12px 40px rgba(0,0,0,0.5)',
              display: 'block',
            }}
          />
        </Grid>

        {/* Text Card */}
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              height: '100%',
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: '20px',
              background: theme.palette.background.paper,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0px 8px 32px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Typography variant="body1" sx={{ lineHeight: 1.85, color: theme.palette.text.primary }}>
              SJ Swimming was founded in 2012 by Susana, who brought her love of swimming from Korea and started teaching privately in New Zealand. When she made the move to Australia, she brought that same passion with her, and SJ has slowly grown from there.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.85, color: theme.palette.text.primary }}>
              We care more about how you swim than how fast you progress through the levels. Our coaches take the time to get to know each swimmer and work at their pace - keeping the balance between hard work and fun, with a focus on technique that actually sticks.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.85, color: theme.palette.text.secondary }}>
              A few of the swimmers we first taught as kids have grown up and come back to coach - which means more to us than we can really put into words. We try to be a place where swimmers feel genuinely known, and we're grateful for the community that's come together around that.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Section for Coaches */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h2"
          sx={{ textAlign: 'center', mt: 5, mb: 1.5, color: theme.palette.text.primary, fontWeight: 700 }}
        >
          Meet Our Team
        </Typography>
        <Box sx={{ width: 48, height: 3, bgcolor: theme.palette.primary.main, mx: 'auto', mb: 4, borderRadius: 1.5 }} />
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Coaches Grid */}
      {!loading && !error && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <Grid container spacing={3}>
            {coaches.map((coach, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  className="coach-card"
                  component={motion.div}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.45, delay: index * 0.1 }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={activeCoach === index}
                  aria-label={`${coach.name} - read bio`}
                  onClick={() => setActiveCoach(activeCoach === index ? null : index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveCoach(activeCoach === index ? null : index);
                    }
                  }}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '20px',
                    background: theme.palette.background.paper,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: theme.palette.text.primary,
                    boxShadow: '0px 8px 32px rgba(0,0,0,0.3)',
                    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                    cursor: 'pointer',
                    height: '220px',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0px 15px 40px rgba(0,0,0,0.7)',
                      '& .coach-bio': { transform: 'translateY(0)', opacity: 1 },
                    },
                  }}
                >
                  {/* Header (Always Visible) */}
                  <Box sx={{ p: 4, textAlign: 'center', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {coach.name}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.8 }}>
                      {coach.title}
                    </Typography>
                  </Box>
                  {/* Tap affordance indicator */}
                  <Box sx={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', zIndex: 2, opacity: activeCoach === index ? 0 : 0.35, transition: 'opacity 0.3s ease', pointerEvents: 'none' }}>
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  </Box>

                  {/* Bio (Slides up on Hover / Tap) */}
                  <Box
                    className="coach-bio"
                    sx={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      bgcolor: '#f7f7f7',
                      color: '#17204a',
                      p: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: activeCoach === index ? 'translateY(0)' : 'translateY(100%)',
                      opacity: activeCoach === index ? 1 : 0,
                      transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      zIndex: 3,
                      overflowY: 'auto',
                    }}
                  >
                    <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 500, lineHeight: 1.6 }}>
                      {coach.bio}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Core Values Section */}
      <CoreValues />
    </Container>
  );
};

export default AboutUs;